import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { type CustomerPairingRatingDetail, StatusCode } from '@hungryroot/rest';
import { patchApiV2CustomersCustomerIdPairingRatingsPairingId } from '@hungryroot/rest/api/v2/customers/[id]/pairing_ratings/[pairing_id]/patch';
import { type LikeDislikeRatings } from '../components/BinaryRatings/BinaryRatings/api';

export interface PairingPayload {
  pairing: number;
  liked: LikeDislikeRatings;
  order_id: number;
  feedback?: string;
}

type UsePatchCustomerPairingRatingsProps<Context> = Omit<
  UseMutationOptions<
    CustomerPairingRatingDetail,
    Error,
    { customerId: number; payload: PairingPayload },
    Context
  >,
  'mutationFn'
>;

export function usePatchCustomerPairingRatings<Context>(
  props?: UsePatchCustomerPairingRatingsProps<Context>
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      customerId: number;
      payload: PairingPayload;
    }) => {
      const { customerId, payload } = params;
      const res = await patchApiV2CustomersCustomerIdPairingRatingsPairingId({
        customerId,
        pairingId: payload.pairing,
        requestBody: payload,
      });
      if (res.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(res.data, null, 2));
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['Customers', 'customers/:id/pairing-ratings/:pairingId'],
      });
    },
    ...props,
  });
}
